import { FC, useEffect, useState } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useForm, Controller } from 'react-hook-form';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useQuery, useMutation } from 'react-query';
import { AdminUserInfoFormInput } from './types';
import { AdminUserProfileInfo } from 'domain/user';
import { Grid, TextField, InputLabel, Select, MenuItem, FormControl, Tooltip } from '@material-ui/core';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import Button from '@material-ui/core/Button';
import { observer } from 'mobx-react-lite';
import { toFormatDateTime } from 'utils';
import { AdminProfileInfo } from 'domain/user/types';
import sampleImg from '../../../assets/sample.svg';
import { getBaseUrl } from 'utils';
import MuiPhoneNumber from 'material-ui-phone-number-2';

export const AdminUserProfileForm: FC = observer(() => {
  const classes = useStyles();
  const [imgFile, setImgFile] = useState<File | undefined | string>('same');
  const {
    userStore: { token, user, getProfile, getProfileInfo, updateProfileInfo, profileInfo },
    endUserSettingsFormStore: { portalSettingsget, endUserConfigSettings },
    notificationStore: { openNotification },
  } = useStore();
  const getUserProfileinfo = useQuery('getUserProfileInfo', () => getProfileInfo(), {
    onError: (e) => {
      openNotification('error', `Can't get profile info`);
    },
  });
  const portalSettingsgetReq = useQuery('portalSettingsgetReq', () => portalSettingsget(), {
    onError: () => openNotification('error', `Can't get data`),
  });
  const updateUserProfileInfoReq = useMutation((data: AdminUserProfileInfo) => updateProfileInfo(data), {
    onSuccess: () => {
      openNotification('success', `Saved!!`);
      getUserProfileinfo.refetch();
      getProfileReq.refetch();
    },
    onError: ({ message }: Error) => openNotification('error', message),
  });
  const getProfileReq = useQuery('getProfileInitialReq', () => getProfile(), {
    enabled: !!token && !(!!user as boolean),
    onError: () => openNotification('error', `Can't get user profile`),
  });
  useAppPreloader(
    getProfileReq.isFetching ||
      getUserProfileinfo.isFetching ||
      updateUserProfileInfoReq.isLoading ||
      portalSettingsgetReq.isFetching
  );
  const { control, handleSubmit } = useForm<AdminUserProfileInfo>();
  const onSubmit = (data: AdminUserProfileInfo) => {
    data.profileImg = imgFile;
    return updateUserProfileInfoReq.mutate(data);
  };
  const [imageSize, setImageSize] = useState(true);
  const organizationBlockMock1: AdminUserInfoFormInput<AdminProfileInfo>[] = [
    { name: 'firstName', title: 'First Name', placeholder: 'First Name', required: true },
    { name: 'lastName', title: 'Last Name', placeholder: 'Last Name', required: true },
    { name: 'email', title: 'Email', placeholder: 'abc@xyz.com', disabled: true },
  ];
  const organizationBlockMock2: AdminUserInfoFormInput<AdminProfileInfo>[] = [
    // { name: 'title', title: 'Title', placeholder: 'Title', required: false },
    // { name: 'department', title: 'Department', placeholder: 'Department', required: false },
    { name: 'phone', title: 'Phone', placeholder: 'Phone number', required: false },
  ];
  const billingContactInfoBlockMock: AdminUserInfoFormInput<AdminProfileInfo>[] = [
    { name: 'organizationName', title: 'Organization Name', placeholder: 'Organization Name', required: true },
    { name: 'address', title: 'Billing Address', placeholder: 'Billing Address', required: true },
    {
      name: 'billingContactInformationFullName',
      title: 'Full Name (Billing Contact)',
      placeholder: 'Full Name',
      required: true,
    },
    // { name: 'billingContactInformationTitle', title: 'Title', placeholder: 'Title', required: false },
    // { name: 'billingContactInformationDepartment', title: 'Department', placeholder: 'Department', required: false },
    { name: 'billingContactInformationEmail', title: 'Email', placeholder: 'Email', required: true },
    { name: 'billingContactInformationPhone', title: 'Phone', placeholder: 'Phone', required: false },
  ];
  const baseUrl = getBaseUrl().split('/', 3).join('/');
  const [imgSrc, setImgSrc] = useState<string>();
  function handleChange(e: { target: { files: (Blob | MediaSource)[] | FileList | any } }) {
    if (e.target.files.length !== 0) {
      setImgFile(e.target.files ? e.target.files[0] : imgFile);
      setImgSrc(URL.createObjectURL(e.target.files[0]));
    }
  }
  useEffect(() => {
    setImgSrc(!getUserProfileinfo.isFetching && profileInfo?.profileUrl ? `${profileInfo?.profileUrl}` : sampleImg);
  }, [baseUrl, getUserProfileinfo.isFetching, profileInfo?.profileUrl]);
  const LightTooltip = () => {
    return (
      <div style={{ display: 'flex', height: '50px' }}>
        <LightbulbIcon />
        <span style={{ lineHeight: '10px' }}>Formats: jpeg, gif, bmp, png. File size (max): 2 MB</span>
      </div>
    );
  };
  return !getUserProfileinfo.isFetching ? (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Grid className={classes.btnContainer}>
        <Tooltip
          title={
            endUserConfigSettings?.profileSelfUpdate === 'false'
              ? 'Profile updates are currently disabled by the administrator.'
              : ''
          }
          arrow
        >
          <span>
            <Button
              className={classes.btn}
              type='submit'
              variant='contained'
              disabled={imageSize === false || endUserConfigSettings?.profileSelfUpdate === 'false'}
            >
              Save Changes
            </Button>
          </span>
        </Tooltip>
      </Grid>
      <div className={classes.formContainer}>
        <div style={{ marginTop: '0px' }} className={classes.heading}>
          General Information
        </div>
        <div className={classes.leftCol}>
          <div className={classes.profileContainer}>
            <img
              className={classes.profilePicture}
              // src={profileInfo?.profileUrl ? `${baseUrl}/${profileInfo?.profileUrl}` : sampleImg}
              src={imgSrc}
              alt='profile'
            />
          </div>
          <div className={classes.uploadBtnContainer}>
            <input
              accept='image/jpeg,image/gif,image/bmp,image/png'
              className={classes.input}
              style={{ display: 'none' }}
              id='profile-picture'
              type='file'
              onChange={(event) => {
                handleChange(event);
                const file = (event.target as HTMLInputElement).files?.[0];
                if (file) {
                  const img = new Image();
                  img.src = window.URL.createObjectURL(file);
                  // img.onload = function () {
                  //   const maxWidth = 400;
                  //   const maxHeight = 400;
                  //   if (img.width > maxWidth || img.height > maxHeight) {
                  //     event.target.value = '';
                  //     setImageSize(false);
                  //     openNotification('error', 'Maximum dimensions allowed are 400px x 400px.');
                  //     (event.target as HTMLInputElement).value = '';
                  //   }
                  // };
                }
              }}
              onInput={(event) => {
                const fileInput = event.target as HTMLInputElement;
                const file = fileInput.files?.[0];
                if (file) {
                  const fileType = file.type;
                  const allowedTypes = ['image/jpeg', 'image/bmp', 'image/gif', 'image/png'];
                  if (!allowedTypes.includes(fileType)) {
                    fileInput.value = '';
                    setImageSize(false);
                    openNotification('error', 'Invalid file type. Only JPEG, BMP, PNG and GIF files are allowed.');
                  } else if (file.size > 2 * 1024 * 1024) {
                    fileInput.value = '';
                    setImageSize(false);
                    openNotification('error', 'Maximum file size allowed is 2 MB.');
                  } else {
                    setImageSize(true);
                  }
                }
              }}
            />
            <label htmlFor='profile-picture'>
              <Tooltip title={<LightTooltip />} classes={{ tooltip: classes.tooltip }} arrow placement='top'>
                <Button component='span' className={classes.link2} variant='contained' color='secondary'>
                  + Upload New Picture
                </Button>
              </Tooltip>
            </label>
          </div>
          <div className={classes.removeBtnContainer}>
            <Button
              className={classes.link}
              variant='outlined'
              onClick={() => {
                setImgFile(undefined);
                setImgSrc(sampleImg);
                setImageSize(true);
              }}
            >
              Remove
            </Button>
          </div>
        </div>
        <div className={classes.rightCol}>
          <Grid container justify='space-between' className={classes.container}>
            <Grid className={classes.block} container>
              {organizationBlockMock1.map((item, index: number) => (
                <Controller
                  key={index}
                  name={item.name}
                  defaultValue={profileInfo?.[item.name]}
                  control={control}
                  rules={{ required: item?.required }}
                  render={({ field, fieldState: { invalid } }) => (
                    <Grid container className={classes.row}>
                      <TextField
                        {...field}
                        label={item.title}
                        disabled={item.disabled || endUserConfigSettings?.profileSelfUpdate === 'false'}
                        error={invalid}
                        placeholder={item.placeholder}
                        helperText={invalid ? 'Required field' : ''}
                        className={classes.input}
                      />
                    </Grid>
                  )}
                />
              ))}
              {organizationBlockMock2.map((item, index: number) =>
                item.name === 'phone' ? (
                  <Controller
                    key={index}
                    name={item.name}
                    defaultValue={profileInfo?.[item.name]}
                    control={control}
                    rules={{ required: item?.required }}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid container className={classes.row}>
                        <FormControl fullWidth>
                          <MuiPhoneNumber
                            {...field}
                            label={item.title}
                            disabled={item.disabled || endUserConfigSettings?.profileSelfUpdate === 'false'}
                            error={invalid}
                            placeholder={item.placeholder}
                            helperText={invalid ? 'Required field' : ''}
                            className={classes.input}
                            data-cy='user-phone'
                            defaultCountry={'us'}
                            variant='standard'
                            color='warning'
                          />
                        </FormControl>
                      </Grid>
                    )}
                  />
                ) : (
                  <Controller
                    key={index}
                    name={item.name}
                    defaultValue={profileInfo?.[item.name]}
                    control={control}
                    rules={{ required: item?.required }}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid container className={classes.row}>
                        <TextField
                          {...field}
                          label={item.title}
                          disabled={item.disabled || endUserConfigSettings?.profileSelfUpdate === 'false'}
                          error={invalid}
                          placeholder={item.placeholder}
                          helperText={invalid ? 'Required field' : ''}
                          className={classes.input}
                        />
                      </Grid>
                    )}
                  />
                )
              )}

              <Controller
                name='lastLogin'
                defaultValue={profileInfo?.lastLogin ? toFormatDateTime(profileInfo?.lastLogin.toString()) : ''}
                control={control}
                render={({ field, fieldState: { invalid } }) => (
                  <Grid container className={classes.row}>
                    <TextField
                      {...field}
                      label='Last Login'
                      disabled
                      error={invalid}
                      placeholder=''
                      helperText={invalid ? 'Required field' : ''}
                      className={classes.input}
                    />
                  </Grid>
                )}
              />
            </Grid>
          </Grid>
          {/* <div className={classes.heading}>Change Password</div>
          <Grid container justify='space-between' className={classes.container}>
            <Grid className={classes.block} container>
              <Controller
                name='oldPassword'
                control={control}
                rules={{ required: false }}
                render={({ field, fieldState: { invalid } }) => (
                  <Grid className={classes.row} container>
                    <FormControl className={classes.input}>
                      <InputLabel htmlFor='oldPassword'>Current Password</InputLabel>
                      <Input
                        {...field}
                        id='oldPassword'
                        name='oldPassword'
                        onChange={field.onChange}
                        error={invalid}
                        type={'password'}
                        key='oldPassword'
                        placeholder='Enter Current Password'
                      />
                    </FormControl>
                  </Grid>
                )}
              />
            </Grid>
          </Grid>
          <Grid container justify='space-between' className={classes.container}>
            <Grid className={classes.block} container>
              <Controller
                name='password'
                control={control}
                rules={{
                  required: false,
                  pattern: {
                    value: new RegExp(
                      `(?=.*[A-Z]{${passUpperCase}})(?=.*[a-z]{${passLowerCase}})(?=.*\\d{${passNumericCase}})(?=.*[!@#$%^&*()_+={}\[\]|\\:;"'<>,.?/~`-]{${passSpecialCase}}).{${minPass},}`
                    ),
                    message: `Password must contain at least ${passUpperCase} uppercase, ${passLowerCase} lowercase, ${passSpecialCase} special characters, ${passNumericCase} numeric value(s), and minimum ${minPass} characters.`,
                  },
                }}
                render={({ field, fieldState }) => (
                  <Grid className={classes.row} container>
                    <FormControl className={classes.input} error={fieldState.invalid}>
                      <InputLabel htmlFor='password'>New Password</InputLabel>
                      <Input
                        {...field}
                        id='password'
                        name='password'
                        onChange={field.onChange}
                        error={fieldState.invalid}
                        type={showPassword ? 'text' : 'password'}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                        placeholder='Enter New Password'
                      />
                      {fieldState.invalid && (
                        <FormHelperText style={{ color: 'red', fontSize: '12px' }}>
                          {fieldState.error?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              />

              <Controller
                name='confirm_password'
                control={control}
                rules={{
                  required: false,
                  validate: (value) => value === watch('password') || 'Passwords do not match',
                }}
                render={({ field, fieldState }) => (
                  <Grid className={classes.row} container>
                    <FormControl className={classes.input} error={fieldState.invalid}>
                      <InputLabel htmlFor='confirmpassword'>Confirm New Password</InputLabel>
                      <Input
                        id='confirmpassword'
                        {...field}
                        onChange={field.onChange}
                        error={fieldState.invalid}
                        type={showConfirmPassword ? 'text' : 'password'}
                        key='confirmpassword'
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                        placeholder='Confirm New Password'
                      />
                      {fieldState.invalid && (
                        <FormHelperText style={{ color: 'red', fontSize: '12px' }}>
                          {fieldState.error?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              />
            </Grid>
          </Grid> */}
          <div className={classes.heading}>Billing Contact Information</div>
          <Grid container justify='space-between' className={classes.container}>
            <Grid className={classes.block} container>
              {billingContactInfoBlockMock.map((item, index) =>
                item.name === 'billingContactInformationPhone' ? (
                  <Controller
                    key={index}
                    name={item.name}
                    defaultValue={profileInfo?.[item.name]}
                    control={control}
                    rules={{ required: item?.required }}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid container className={classes.row}>
                        <FormControl fullWidth>
                          <MuiPhoneNumber
                            {...field}
                            label={item.title}
                            error={invalid}
                            placeholder={item.placeholder}
                            disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                            helperText={invalid ? 'Required field' : ''}
                            className={classes.input}
                            data-cy='user-phone'
                            defaultCountry={'us'}
                            variant='standard'
                            color='warning'
                          />
                        </FormControl>
                      </Grid>
                    )}
                  />
                ) : (
                  <Controller
                    key={index}
                    name={item.name}
                    defaultValue={profileInfo?.[item.name] === 'null' ? '' : profileInfo?.[item.name]}
                    control={control}
                    rules={{ required: item?.required }}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid container className={classes.row}>
                        <TextField
                          {...field}
                          label={item.title}
                          error={invalid}
                          placeholder={item.placeholder}
                          helperText={invalid ? 'Required field' : ''}
                          className={classes.input}
                          disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                        />
                      </Grid>
                    )}
                  />
                )
              )}
              <Controller
                name='salesTaxGstVatExemptOrganization'
                defaultValue={profileInfo?.salesTaxGstVatExemptOrganization === 1 ? '1' : '0'}
                control={control}
                rules={{ required: true }}
                disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                render={({ field, fieldState: { invalid } }) => (
                  <Grid className={classes.row} container>
                    <FormControl className={classes.input}>
                      <InputLabel className={classes.title} id='demo-simple-select-label'>
                        Sales Tax/VAT Exempt
                      </InputLabel>
                      <Select {...field} error={invalid} className={classes.input}>
                        <MenuItem value={'0'}>No</MenuItem>
                        <MenuItem value={'1'}>Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </form>
  ) : null;
});
